import { useMemo, useState } from 'react';
import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import {
  CatalogFormulaDto,
  CatalogItemDto,
  createOrder,
  fetchCatalog,
} from '../api/candidate';

const eur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const UNAVAILABLE = 'Commande impossible pour le moment.';

function Stepper({
  qty,
  onChange,
}: {
  qty: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, qty - 1))}
        className="w-8 h-8 rounded-lg border border-slate-300 text-slate-600 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40"
        disabled={qty === 0}
        aria-label="Retirer"
      >
        <Minus size={15} />
      </button>
      <span className="w-6 text-center text-sm font-semibold text-slate-900">
        {qty}
      </span>
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        className="w-8 h-8 rounded-lg border border-slate-300 text-slate-600 flex items-center justify-center hover:bg-slate-50"
        aria-label="Ajouter"
      >
        <Plus size={15} />
      </button>
    </div>
  );
}

export default function CommandView() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'catalog'],
    queryFn: fetchCatalog,
  });

  // Quantités par identifiant (clé préfixée pour distinguer article/formule).
  const [qty, setQty] = useState<Record<string, number>>({});

  const setItemQty = (key: string, n: number) =>
    setQty((prev) => ({ ...prev, [key]: n }));

  const cartTotal = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const it of data.items)
      total += (qty[`item:${it.id}`] ?? 0) * it.priceInclTax;
    for (const f of data.formulas)
      total += (qty[`formula:${f.id}`] ?? 0) * f.priceInclTax;
    return total;
  }, [data, qty]);

  const lineCount = Object.values(qty).filter((n) => n > 0).length;

  const orderMutation = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('Catalogue indisponible.');
      const items = data.items
        .map((it) => ({
          catalogItemId: it.id,
          quantity: qty[`item:${it.id}`] ?? 0,
        }))
        .filter((l) => l.quantity > 0);
      const formulas = data.formulas
        .map((f) => ({
          catalogFormulaId: f.id,
          quantity: qty[`formula:${f.id}`] ?? 0,
        }))
        .filter((l) => l.quantity > 0);
      return createOrder({ items, formulas });
    },
    onSuccess: (res) => {
      // La commande se règle tout de suite : la facture n'est émise qu'une fois payée.
      if (res.checkoutUrl) window.location.href = res.checkoutUrl;
      else window.alert(UNAVAILABLE);
    },
    onError: (e: unknown) => {
      const message = axios.isAxiosError(e) ? e.response?.data?.message : null;
      window.alert(typeof message === 'string' ? message : UNAVAILABLE);
    },
  });

  if (isLoading) {
    return <p className="text-slate-500">Chargement du catalogue…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-red-600">Impossible de charger le catalogue.</p>
    );
  }

  const hasCatalog = data.items.length > 0 || data.formulas.length > 0;
  // Sans paiement en ligne, le catalogue se consulte mais rien ne se commande.
  const canOrder = data.paymentEnabled;

  return (
    <section className="space-y-6 pb-28">
      <div>
        <h1 className="text-slate-900">Commander</h1>
        <p className="text-slate-600">
          {canOrder
            ? 'Choisissez vos prestations et réglez-les en ligne : votre facture vous est envoyée dès le paiement.'
            : 'Les prestations proposées par votre auto-école.'}
        </p>
      </div>

      {hasCatalog && !canOrder && (
        <div
          role="status"
          className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          La commande en ligne n'est pas disponible : votre auto-école n'a pas
          encore activé le paiement en ligne. Adressez-vous à elle pour
          commander ces prestations.
        </div>
      )}

      {!hasCatalog && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Le catalogue de votre auto-école n'est pas encore disponible.
        </div>
      )}

      {data.formulas.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-slate-900 mb-4">Formules</h2>
          <div className="divide-y divide-slate-100">
            {data.formulas.map((f: CatalogFormulaDto) => (
              <div key={f.id} className="flex items-center gap-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {f.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {eur(f.priceInclTax)} TTC · {f.itemsCount} article(s)
                  </div>
                </div>
                {canOrder && (
                  <Stepper
                    qty={qty[`formula:${f.id}`] ?? 0}
                    onChange={(n) => setItemQty(`formula:${f.id}`, n)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-slate-900 mb-4">Articles</h2>
          <div className="divide-y divide-slate-100">
            {data.items.map((it: CatalogItemDto) => (
              <div key={it.id} className="flex items-center gap-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {it.name}
                  </div>
                  {it.description && (
                    <p className="mt-0.5 whitespace-pre-line text-xs leading-snug text-slate-600">
                      {it.description}
                    </p>
                  )}
                  <div className="text-xs text-slate-500">
                    {eur(it.priceInclTax)} TTC
                    {it.hours > 0 ? ` · ${it.hours} h` : ''}
                  </div>
                </div>
                {canOrder && (
                  <Stepper
                    qty={qty[`item:${it.id}`] ?? 0}
                    onChange={(n) => setItemQty(`item:${it.id}`, n)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barre de panier fixe */}
      {canOrder && lineCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-xs text-slate-500">
                {lineCount} ligne(s)
              </div>
              <div className="text-lg font-bold text-slate-900">
                {eur(cartTotal)}
              </div>
            </div>
            <button
              onClick={() => orderMutation.mutate()}
              disabled={orderMutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              <ShoppingCart size={18} />
              {orderMutation.isPending ? 'Redirection…' : 'Payer ma commande'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
